import { useState } from "react";
import { isAxiosError } from "axios";
import { Loader2 } from "lucide-react";
import {
  recalculateCompanyAssessment,
  saveCompanyEvidence,
  type CompanyAssessmentDetail,
  type CompanyEvidenceReview,
  type GrowthDetail,
} from "@/api/usGrowth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  assessmentNext,
  assessmentReason,
  assessmentScore,
  decisionLabels,
  dimensionLabels,
  dimensionReason,
  qualityLabels,
} from "@/lib/companyAssessment";
import { dateLabel, growthScoreRuleLabel } from "@/lib/usGrowth";

const editable: CompanyEvidenceReview["dimension"][] = [
  "finance_team_level",
  "has_hr_lead",
  "has_cos_coo",
  "hiring_finance_level",
  "hiring_stratops",
  "hiring_hr",
  "is_excluded_incubator",
  "is_blacklisted_sector",
];

export function UsGrowthAssessment({
  assessment: a,
  slug,
  companyId,
  onSaved,
}: {
  assessment: CompanyAssessmentDetail;
  slug: string;
  companyId: number;
  onSaved: (data: GrowthDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dimension, setDimension] =
    useState<CompanyEvidenceReview["dimension"]>("finance_team_level");
  const [value, setValue] = useState("");
  const [url, setUrl] = useState("");
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [coverage, setCoverage] = useState<"complete" | "partial">("partial");
  const [editing, setEditing] = useState(false);
  const choices =
    dimension === "finance_team_level"
      ? [
          ["cfo", "CFO en poste"],
          ["senior", "Responsable finance"],
          ["junior", "Équipe junior"],
          ["none", "Non observé après recherche complète"],
        ]
      : dimension === "hiring_finance_level"
        ? [
            ["senior_cfo", "Offre CFO"],
            ["senior", "Offre responsable finance"],
            ["junior", "Offre finance junior"],
            ["none", "Non observé après recherche complète"],
          ]
        : [
            ["true", "Présence documentée"],
            ["false", "Non observé après recherche complète"],
          ];
  async function perform(operation: () => Promise<GrowthDetail>) {
    setBusy(true);
    setError("");
    try {
      const data = await operation();
      onSaved(data);
      setEditing(false);
    } catch (e) {
      setError(
        isAxiosError(e) && e.response?.status === 409
          ? "Le dossier a changé. Actualisez avant d’enregistrer."
          : isAxiosError(e) && e.response?.status === 422
            ? "Vérifiez la date, la preuve et la couverture. Une recherche partielle ne permet pas de conclure une absence."
            : "Impossible d’enregistrer l’évaluation. Réessayez.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      className="rounded-xl border border-slate-200 bg-slate-50/50 p-4"
      aria-label="Diagnostic CFO de la société"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">
            Diagnostic CFO
          </h4>
          <p className="mt-1 text-sm font-medium text-slate-700">
            {decisionLabels[a.decision]}
          </p>
          <p className="mt-1 text-xs text-slate-500">
            {a.complete_dimensions} / {a.total_dimensions} dimensions
            documentées · {dateLabel(a.evaluated_at)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xl font-semibold tabular-nums text-slate-900">
            {a.score ?? "—"}
          </p>
          <p className="text-xs text-slate-500">
            {a.score === null
              ? "Non applicable"
              : a.provisional
                ? "Score provisoire"
                : "Score complet"}
          </p>
        </div>
      </div>
      <div
        className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-label="Complétude des données CFO"
        aria-valuenow={a.complete_dimensions}
        aria-valuemin={0}
        aria-valuemax={a.total_dimensions}
      >
        <div
          className="h-full rounded-full bg-indigo-500"
          style={{
            width: `${a.total_dimensions ? (100 * a.complete_dimensions) / a.total_dimensions : 0}%`,
          }}
        />
      </div>
      {a.funding && (
        <p className="mt-3 text-xs text-slate-600">
          Dernier tour documenté : {a.funding.stage.replace(/_/g, " ")} ·{" "}
          {dateLabel(a.funding.announced_on)}
          {a.funding.amount_usd !== null
            ? ` · ${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(a.funding.amount_usd)}`
            : " · montant inconnu"}
        </p>
      )}
      <p className="mt-3 text-sm text-slate-700">{assessmentNext(a)}</p>
      {!a.current && (
        <p className="mt-2 text-xs text-amber-800">
          Une source, une règle ou la fraîcheur a changé. Le calcul doit être
          actualisé.
        </p>
      )}
      <details className="mt-4">
        <summary className="cursor-pointer text-xs font-medium text-indigo-600">
          Données, preuves et calcul
        </summary>
        <div className="mt-3 space-y-2">
          {[...a.dimensions]
            .sort(
              (x, y) =>
                Number(x.status === "COMPLETE") -
                Number(y.status === "COMPLETE"),
            )
            .map((d) => (
              <details
                key={d.dimension}
                className="rounded-lg border bg-white p-3"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-xs">
                  <span className="font-medium">
                    {dimensionLabels[d.dimension] ?? d.dimension}
                  </span>
                  <span
                    className={
                      d.status === "COMPLETE"
                        ? "text-emerald-700"
                        : "text-amber-800"
                    }
                  >
                    {qualityLabels[d.status]}
                  </span>
                </summary>
                <p className="mt-2 text-xs text-slate-600">
                  {dimensionReason(d.reason)}
                </p>
                {d.observed_at && (
                  <p className="mt-2 text-xs text-slate-500">
                    Observé le {dateLabel(d.observed_at)}
                    {d.valid_until
                      ? ` · valable jusqu’au ${dateLabel(d.valid_until)}`
                      : ""}
                  </p>
                )}
                {d.sources.map((s, i) => (
                  <p key={i} className="mt-2 text-xs text-slate-500">
                    {s.url && /^https?:\/\//.test(s.url) ? (
                      <a
                        className="text-indigo-600 hover:underline"
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Ouvrir la preuve {i + 1}
                      </a>
                    ) : (
                      "Preuve historique conservée"
                    )}{" "}
                    · {s.source === "manual" ? "Correction sourcée" : s.source}{" "}
                    {s.observed_at ? `· ${dateLabel(s.observed_at)}` : ""}
                  </p>
                ))}
                {!!d.investigations.length && (
                  <details className="mt-3 text-xs text-slate-500">
                    <summary className="cursor-pointer">
                      Recherches effectuées ({d.investigations.length})
                    </summary>
                    <ul className="mt-2 space-y-2">
                      {d.investigations.map((r, i) => (
                        <li key={i}>
                          <p className="font-medium">
                            {r.source} · {dateLabel(r.observed_at)}
                          </p>
                          <p>{r.detail}</p>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {a.can_review &&
                  editable.includes(
                    d.dimension as CompanyEvidenceReview["dimension"],
                  ) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setDimension(
                          d.dimension as CompanyEvidenceReview["dimension"],
                        );
                        setValue("");
                        setEditing(true);
                      }}
                    >
                      Ajouter une preuve
                    </Button>
                  )}
              </details>
            ))}
        </div>
        <details className="mt-4 text-xs">
          <summary className="cursor-pointer font-medium text-slate-700">
            Calcul du score : {assessmentScore(a)}
          </summary>
          <ul className="mt-2 divide-y">
            {a.components.map((c) => (
              <li key={c.key} className="flex justify-between gap-3 py-2">
                <span>{growthScoreRuleLabel(c.key, c.label)}</span>
                <span className="shrink-0 text-slate-500">
                  {c.status === "DISABLED"
                    ? "Désactivé"
                    : c.contribution === null
                      ? "À documenter"
                      : `${c.contribution} / ${c.weight} pts${c.status !== "COMPLETE" ? " · provisoire" : ""}`}
                </span>
              </li>
            ))}
          </ul>
          {a.provisional &&
            a.score_lower !== null &&
            a.score_upper !== null && (
              <p className="mt-2 text-slate-500">
                Fourchette selon les données manquantes : {a.score_lower} à{" "}
                {a.score_upper} points. Une exclusion reste possible tant que
                ses preuves ne sont pas complètes.
              </p>
            )}
          <p className="mt-2 text-slate-500">
            Seuil CFO : {a.threshold ?? "non configuré"}. Barème du projet. Une
            valeur provisoire ne constitue pas une qualification.
          </p>
        </details>
        {!!a.reasons.length && (
          <details className="mt-4 text-xs text-slate-500">
            <summary className="cursor-pointer">Motifs de la décision</summary>
            <ul className="mt-2 space-y-1">
              {a.reasons.map((r) => (
                <li key={r}>{assessmentReason(r)}</li>
              ))}
            </ul>
          </details>
        )}
        <p className="mt-4 text-xs text-slate-500">
          La vérification des founders et l’admissibilité à la prospection
          restent indépendantes de ce diagnostic.
        </p>
      </details>
      {a.can_review && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => setEditing(!editing)}
          >
            Ajouter une preuve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() =>
              perform(() => recalculateCompanyAssessment(slug, companyId, a.id))
            }
          >
            {busy && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            Recalculer sans dépense
          </Button>
        </div>
      )}
      {editing && (
        <form
          className="mt-4 space-y-3 rounded-lg border bg-white p-3"
          onSubmit={(e) => {
            e.preventDefault();
            void perform(() =>
              saveCompanyEvidence(slug, companyId, {
                expected_assessment_id: a.id,
                dimension,
                value,
                source_url: url,
                observed_at: new Date(date).toISOString(),
                coverage,
                reason,
              }),
            );
          }}
        >
          <p className="text-sm font-medium">Nouvelle preuve de société</p>
          <Select
            value={dimension}
            onValueChange={(v) => {
              setDimension(v as CompanyEvidenceReview["dimension"]);
              setValue("");
            }}
          >
            <SelectTrigger aria-label="Critère CFO">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {editable.map((d) => (
                <SelectItem value={d} key={d}>
                  {dimensionLabels[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={value} onValueChange={setValue}>
            <SelectTrigger aria-label="Valeur documentée">
              <SelectValue placeholder="Choisir la valeur documentée" />
            </SelectTrigger>
            <SelectContent>
              {choices.map(([v, l]) => (
                <SelectItem value={v} key={v}>
                  {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="url"
            required
            aria-label="URL de la preuve"
            placeholder="Lien vers la preuve"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <label className="block text-xs text-slate-500">
            Date d’observation
            <Input
              type="datetime-local"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <Select
            value={coverage}
            onValueChange={(v) => setCoverage(v as "complete" | "partial")}
          >
            <SelectTrigger aria-label="Couverture de la recherche">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="partial">
                Preuve ponctuelle / recherche partielle
              </SelectItem>
              <SelectItem value="complete">
                Recherche complète dans la source
              </SelectItem>
            </SelectContent>
          </Select>
          <Textarea
            required
            minLength={12}
            maxLength={1000}
            aria-label="Justification et périmètre"
            placeholder="Ce que la preuve établit ; périmètre, pages et limites de la recherche"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="text-xs text-slate-500">
            Une page sans résultat ne prouve pas une absence. Cette correction
            n’effectue aucune vérification d’email.
          </p>
          <Button
            type="submit"
            size="sm"
            disabled={
              busy || !value || !url || !date || reason.trim().length < 12
            }
          >
            Enregistrer et recalculer
          </Button>
        </form>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
